/**
 * Error reporting to the self-hosted Sentry at sentry.servicepilot.com.au.
 * NFR-07, and the last unbuilt line of it.
 *
 * Until this existed an exception in somebody's browser was recorded NOWHERE.
 * Not in the gate, not in a log, not anywhere. The whole history of this build
 * is bugs that looked like nothing: a blank page with a silent console (R-143),
 * an empty 200 with no markup (R-182), a route that suspended for ever because
 * a query paused instead of failing (R-144). Every one of those was found by a
 * person looking at a screen. This is the first thing that would have told us.
 *
 * ── WHAT THIS REFUSES TO SEND ────────────────────────────────────────────────
 *
 * This system holds the client's customers: names, addresses, artwork, order
 * values, and the cost of every job. Self-hosting means it stays on our own
 * server rather than a vendor's, which is better and is not a reason to be
 * careless.
 *
 *   • `sendDefaultPii: false` — no IP address, no cookies, no request bodies.
 *   • `beforeSend` strips the query string and hash off every URL. The order
 *     and quote pages take a `?t=` TOKEN that is a bearer credential: anyone
 *     holding it can read that customer's order. It must never sit in an error
 *     report. Same for `?token=` on the password reset, which CHANGES A
 *     PASSWORD.
 *   • `beforeBreadcrumb` drops console arguments and fetch bodies, which is
 *     where a customer's details would otherwise arrive by the back door.
 *   • The user is reduced to an id. Which person hit a bug is worth knowing;
 *     their email address is not needed to fix anything.
 *
 * The result says WHERE and WHAT, not WHO.
 */
import * as Sentry from '@sentry/react';
// Set by the deploy. Unset is fine: issues then group without a version. The
// sidebar shows the same value, so one release has one name (src/lib/version.ts).
import { RELEASE } from './version';

/**
 * The PUBLIC_ prefix on a process variable, and NOT `import.meta.env`,
 * which is the Vite spelling
 * and is wrong here.
 *
 * This application is bundled by BUN at run time, not by Vite. Bun leaves
 * `import.meta.env.X` in the output verbatim, so in a browser it evaluates
 * `undefined.X` and throws while the module is still loading. That does not
 * break Sentry, it breaks the ENTIRE APPLICATION, at the first line of the
 * first import, with a white page.
 *
 * Bun inlines a process variable by name instead, for names matching the prefix in
 * `bunfig.toml`'s `[serve.static] env`. The prefix is `PUBLIC_` precisely
 * because everything it matches is compiled into a file anyone can download:
 * a name that says so is the guard against putting a secret there.
 */
const DSN = process.env.PUBLIC_SENTRY_DSN;


/**
 * A token in a query string is a credential, and these are the names this
 * application uses for them. `t` is the order and quote share token; `token` is
 * the password reset.
 */
const SECRET_PARAMS = ['t', 'token', 'access_token', 'refresh_token', 'code'];

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    for (const p of SECRET_PARAMS) {
      if (u.searchParams.has(p)) u.searchParams.set(p, '[redacted]');
    }
    // The hash carries the Supabase implicit-flow tokens. Nothing in it helps
    // diagnose a stack trace.
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return url.split('#')[0];
  }
}

export function initSentry(): void {
  // No DSN means reporting is off. That is correct in local development and in
  // any build made before the project existed, and it must never be a crash on
  // startup: a monitoring tool that can take the application down with it is
  // worse than no monitoring tool.
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    // Derived from the address, NOT from the NODE_ENV process variable.
    //
    // Bun inlines only the names matching the `PUBLIC_*` prefix. Every OTHER
    // An un-inlined process variable survives into the bundle verbatim, and
    // `process` does not
    // exist in a browser, so it throws ReferenceError while this module loads
    // and the whole application renders nothing.
    //
    // That shipped, from this exact line, minutes after a test was added to
    // catch the Vite spelling of the same mistake. The test checked
    // `import.meta.env` and not this. `window.location` needs no build step and
    // cannot be undefined here.
    environment: /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
      ? 'development'
      : 'production',
    // Ties an error to a deploy, which turns "when did this start" into a
    // lookup rather than an argument.
    //
    // There are NO SOURCE MAPS, and that is a deliberate gap rather than an
    // oversight. `@sentry/vite-plugin` uploads maps for the `vite build`
    // output, and this application is not served from it: `server/index.ts`
    // imports index.html and BUN bundles at run time. Maps from the Vite build
    // would describe a bundle nobody is running, which is worse than none,
    // because a stack trace would point confidently at the wrong line. Wiring
    // Bun's own maps is a follow-up.
    //
    // So a trace reads `a.j is not a function`. The error type, the message,
    // the breadcrumbs and the URL are still there, and those are what say
    // WHICH screen broke and WHAT the person did first.
    release: RELEASE,

    sendDefaultPii: false,

    // Sampled. Full tracing on every page load tells us nothing a tenth of it
    // would not, and this runs on a workshop's tablet over a phone connection.
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],

    // A ceiling on volume. One render loop must not fill the disk on a server
    // that is also holding four other applications' issues.
    maxBreadcrumbs: 30,

    beforeSend(event) {
      if (event.request?.url) event.request.url = scrubUrl(event.request.url);
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
      }
      if (event.user) event.user = { id: event.user.id };
      for (const c of event.breadcrumbs ?? []) {
        if (typeof c.data?.url === 'string') c.data.url = scrubUrl(c.data.url);
      }
      return event;
    },

    beforeBreadcrumb(crumb) {
      if (crumb.category === 'console') {
        return { ...crumb, message: crumb.message?.slice(0, 200), data: undefined };
      }
      if (typeof crumb.data?.url === 'string') {
        return { ...crumb, data: { ...crumb.data, url: scrubUrl(crumb.data.url) } };
      }
      return crumb;
    },

    // Not ours and not actionable: a browser extension throwing inside our
    // page, and the ResizeObserver notice every Chrome build emits.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  });
}

/**
 * Attach the signed-in person by id only.
 *
 * `role` is a tag rather than a field because the first question about any new
 * issue here is "does this hit staff or only admins", and that decides whether
 * the workshop stops.
 */
export function identifySentryUser(userId: string | null, role: string | null): void {
  if (!DSN) return;
  if (!userId) {
    Sentry.setUser(null);
    Sentry.setTag('role', undefined);
    return;
  }
  Sentry.setUser({ id: userId });
  if (role) Sentry.setTag('role', role);
}

/** Report something caught and handled, where silence would hide a real fault. */
export function reportError(e: unknown, context?: Record<string, unknown>): void {
  if (!DSN) return;
  Sentry.captureException(e, context ? { extra: context } : undefined);
}

export { Sentry };
